export class ApiError extends Error {
  status: number;
  detail: unknown;
  constructor(status: number, detail: unknown) {
    super(typeof detail === "string" ? detail : `HTTP ${status}`);
    this.status = status;
    this.detail = detail;
  }
}

export const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";

let bouncing = false;

/** NEXTnet mode: an expired/missing session answers 401 with the login URL; send the browser there once. */
function bounceToLogin(detail: unknown) {
  if (bouncing || typeof detail !== "object" || detail === null) return;
  const login = (detail as { login_url?: string }).login_url;
  if (!login) return;
  bouncing = true;
  const url = new URL(login, window.location.origin);
  // NEXTnet auth contract (same as AGS Studio): the login page reads `?url=` and redirects back there
  // once the shared session cookie is set.
  url.searchParams.set("url", window.location.href);
  window.location.assign(url.toString());
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers ?? {});
  if (init.body && !(init.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(`${API_BASE}${path}`, { credentials: "include", ...init, headers });
  if (!res.ok) {
    let detail: unknown = await res.text();
    try {
      detail = JSON.parse(detail as string).detail ?? detail;
    } catch {
      /* plain text */
    }
    if (res.status === 401) bounceToLogin(detail);
    throw new ApiError(res.status, detail);
  }
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get("content-type") ?? "";
  if (ct.includes("text/html")) {
    // The API never answers with HTML; this is a SPA fallback or a proxy pointing at the wrong server.
    throw new ApiError(res.status, `unexpected HTML from ${API_BASE}${path} — is the API server running the current code?`);
  }
  return (ct.includes("application/json") ? res.json() : res.text()) as Promise<T>;
}

export const api = {
  get: <T>(path: string) => apiFetch<T>(path),
  post: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: "PUT", body: body === undefined ? undefined : JSON.stringify(body) }),
  del: <T>(path: string) => apiFetch<T>(path, { method: "DELETE" }),
};
